// components/estimates/EstimateCategoryContent.js
// Contenuto della categoria attiva con tabella voci

import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Calculator, Lock, Check, X, ArrowDownToLine, Ruler, GripVertical, ChevronUp, ChevronDown, ChevronRight, ArrowRightLeft } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { parseItalianNumber, normalizeNumericInput, formatItalianNumber } from '../../lib/numericUtils';
import FormulaInput from '../FormulaInput';
import MeasurementLinesEditor, { computeTotalFromMeasurementLines } from './MeasurementLinesEditor';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CATEGORIES = [
  { code: 'A', label: 'Progettazione', color: '#FF7819' },
  { code: 'B', label: 'Pratiche', color: '#FF7819' },
  { code: 'C', label: 'Opere edili', color: '#FF7819' },
  { code: 'D', label: 'Impianti elettrici', color: '#FF7819' },
  { code: 'E', label: 'Impianti idraulici, termici e climatizzazione', color: '#FF7819' },
  { code: 'F', label: 'Finiture', color: '#FF7819' },
  { code: 'G', label: 'Serramenti', color: '#FF7819' },
  { code: 'H', label: 'Prodotti', color: '#FF7819' },
  { code: 'S', label: 'Sicurezza', color: '#FF7819' },
];

// === FASE 5 - raggruppamento elettrico (categoria D) per ambiente ===
const CAPITOLATO_CODE = 'CAP-EL';
const isElectricalIntro = (it) => it.line_type === 'section_intro' || it.code === CAPITOLATO_CODE;
const hasElectricalStructure = (items) =>
  (items || []).some((it) => isElectricalIntro(it) || it.electrical_room_key || it.electrical_group_id || it.electrical_scope);

const buildElectricalGroups = (items) => {
  const intro = [];
  const map = new Map();
  (items || []).forEach((it) => {
    if (isElectricalIntro(it)) { intro.push(it); return; }
    const key = it.electrical_room_key || 'altre';
    const label = it.electrical_room_label || 'Altre lavorazioni elettriche';
    if (!map.has(key)) map.set(key, { key, label, items: [] });
    map.get(key).items.push(it);
  });
  const rank = (k) => (k === 'generali' ? 3 : k === 'altre' ? 2 : 1);
  const groups = Array.from(map.values()).sort((a, b) => rank(a.key) - rank(b.key));
  groups.forEach((g) => { g.subtotal = g.items.reduce((s, i) => s + (i.total || 0), 0); });
  return { intro, groups };
};

// Definizione unica delle larghezze colonne condivisa tra header e righe (via <colgroup>).
// Layout ottenuto tramite ridistribuzione grid-template-columns su tabella table-fixed:
//   - Tutte le colonne economiche + Azioni + N° hanno larghezze minime stabili in px,
//     dimensionate per non troncare "a corpo", separare visivamente Prezzo Unit./Totale
//     e ospitare le icone azione senza assorbire spazio in eccesso.
//   - La colonna Codice-Descrizione usa 1fr (auto) e occupa lo spazio residuo,
//     mediamente ~60-62% del container (che è capped a 1563 px su desktop).
//   - Il wrapping delle descrizioni lunghe avviene naturalmente grazie a table-fixed.
//   - Nessun margin negativo, transform o positioning assoluto.
//   - Il wrapper .overflow-x-auto garantisce lo scroll orizzontale su viewport stretti.
const ESTIMATE_TABLE_COLS = [
  { key: 'num',        width: '40px'  },  // N° / drag handle
  { key: 'desc',       width: 'auto'  },  // Codice + Descrizione (1fr, ~60%)
  { key: 'unit',       width: '80px'  },  // U.M. — spazio per "a corpo" su 1 riga
  { key: 'quantity',   width: '100px' },  // Q.tà
  { key: 'unit_price', width: '120px' },  // Prezzo Unit.
  { key: 'total',      width: '124px' },  // Totale
  { key: 'actions',    width: '128px' },  // Azioni — icone senza absorb
];

const EstimateCategoryContent = ({
  activeCategory,
  activeCategoryItems,
  categoryTotals,
  formData,
  editingNoteId,
  setEditingNoteId,
  editingNoteValue,
  setEditingNoteValue,
  onShowAddItemModal,
  onAddBelow,
  onUpdateItem,
  onRemoveItem,
  onCategoryNotesChange,
  onCategoryDiscountChange,
  onCategoryFixedPriceChange,
  // V1 Riordino voci intra-categoria (opzionale)
  canReorder = false,
  onReorder,
  onAddItemToRoom,
}) => {
  // Hooks devono essere chiamati SEMPRE prima di early return
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [collapsedGroups, setCollapsedGroups] = useState({});

  if (!activeCategory) return null;

  const currentCategoryLabel = CATEGORIES.find(c => c.code === activeCategory)?.label;
  const useElectricalGroups = activeCategory === 'D' && hasElectricalStructure(activeCategoryItems);

  const handleMoveRoom = (itemId, key, label) => {
    const item = activeCategoryItems.find((i) => i.id === itemId);
    // Riusa il group_id già presente nell'ambiente di destinazione, se esiste
    const existingInTarget = activeCategoryItems.find(
      (i) => i.id !== itemId && i.electrical_room_key === key && i.electrical_group_id
    );
    // Altrimenti ricava il preset dalle righe D del preventivo (non 'personalizzato')
    const preset = item?.electrical_preset
      || activeCategoryItems.find((i) => i.electrical_preset)?.electrical_preset
      || 'personalizzato';
    const scope = (key === 'generali' || key === 'altre') ? 'general' : 'room';
    const groupId = existingInTarget?.electrical_group_id || `${preset}:${key}`;
    onUpdateItem(itemId, 'electrical_room_key', key);
    onUpdateItem(itemId, 'electrical_room_label', label);
    onUpdateItem(itemId, 'electrical_scope', scope);
    onUpdateItem(itemId, 'electrical_group_id', groupId);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = activeCategoryItems.findIndex((it) => it.id === active.id);
    const newIndex = activeCategoryItems.findIndex((it) => it.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(activeCategoryItems, oldIndex, newIndex);
    onReorder && onReorder(activeCategory, reordered.map((it) => it.id));
  };

  const handleMove = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= activeCategoryItems.length) return;
    const reordered = arrayMove(activeCategoryItems, fromIndex, toIndex);
    onReorder && onReorder(activeCategory, reordered.map((it) => it.id));
  };

  return (
    <div className="space-y-4">
      {/* Category Header */}
      <Card className="border-l-4 border-l-[#FF7819]">
        <CardHeader className="py-3 px-5 bg-[#FF7819]">
          <CardTitle className="text-white flex items-center justify-between">
            <span className="flex items-baseline gap-2">
              <span className="text-[15px] font-semibold text-white/70">{activeCategory}</span>
              <span className="text-white/50">·</span>
              <span className="text-[19px] font-semibold tracking-tight">{currentCategoryLabel}</span>
            </span>
            <Button 
              size="sm" 
              variant="secondary"
              className="bg-white text-[#FF7819] hover:bg-white/90"
              onClick={onShowAddItemModal}
              data-testid="add-item-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi voce
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Items Table */}
          {activeCategoryItems.length > 0 ? (
            useElectricalGroups ? (
            <div>
              <ElectricalGroupedView
                items={activeCategoryItems}
                collapsedGroups={collapsedGroups}
                setCollapsedGroups={setCollapsedGroups}
                editingNoteId={editingNoteId}
                setEditingNoteId={setEditingNoteId}
                editingNoteValue={editingNoteValue}
                setEditingNoteValue={setEditingNoteValue}
                onUpdateItem={onUpdateItem}
                onRemoveItem={onRemoveItem}
                onAddBelow={onAddBelow}
                onMoveRoom={handleMoveRoom}
                onAddItemToRoom={onAddItemToRoom}
              />
              <div className="mt-3 pt-3 border-t border-dashed border-[#E5E5E5] flex justify-center">
                <Button variant="outline" size="sm" onClick={onShowAddItemModal}
                  className="border-[#FF7819] text-[#FF7819] hover:bg-[#FF7819]/10" data-testid="add-item-btn-bottom-electrical">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi voce
                </Button>
              </div>
            </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed" data-testid="estimate-items-table">
                <colgroup>
                  {ESTIMATE_TABLE_COLS.map((col) => (
                    <col key={col.key} style={{ width: col.width }} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="border-b border-[#DCDCDC]">
                    <th className="text-left py-2 px-2 font-medium text-[#737373]">N°</th>
                    <th className="text-left py-2 px-2 font-medium text-[#737373]">Codice - Descrizione</th>
                    <th className="text-center py-2 px-2 font-medium text-[#737373]">U.M.</th>
                    <th className="text-center py-2 px-2 font-medium text-[#737373]">Q.tà</th>
                    <th className="text-right py-2 px-2 font-medium text-[#737373]">Prezzo Unit.</th>
                    <th className="text-right py-2 px-2 font-medium text-[#737373]">Totale</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {canReorder ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={activeCategoryItems.map((it) => it.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {activeCategoryItems.map((item, index) => (
                          <SortableItemRow
                            key={item.id}
                            item={item}
                            index={index}
                            isFirst={index === 0}
                            isLast={index === activeCategoryItems.length - 1}
                            canReorder={canReorder}
                            onMoveUp={() => handleMove(index, index - 1)}
                            onMoveDown={() => handleMove(index, index + 1)}
                            editingNoteId={editingNoteId}
                            setEditingNoteId={setEditingNoteId}
                            editingNoteValue={editingNoteValue}
                            setEditingNoteValue={setEditingNoteValue}
                            onUpdateItem={onUpdateItem}
                            onRemoveItem={onRemoveItem}
                            onAddBelow={onAddBelow}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    activeCategoryItems.map((item, index) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        index={index}
                        canReorder={false}
                        editingNoteId={editingNoteId}
                        setEditingNoteId={setEditingNoteId}
                        editingNoteValue={editingNoteValue}
                        setEditingNoteValue={setEditingNoteValue}
                        onUpdateItem={onUpdateItem}
                        onRemoveItem={onRemoveItem}
                        onAddBelow={onAddBelow}
                      />
                    ))
                  )}
                </tbody>
              </table>
              
              {/* STEP 1: Pulsante "Aggiungi voce" anche in fondo alla tabella */}
              <div className="mt-3 pt-3 border-t border-dashed border-[#E5E5E5] flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onShowAddItemModal}
                  className="border-[#FF7819] text-[#FF7819] hover:bg-[#FF7819]/10"
                  data-testid="add-item-btn-bottom"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi voce in fondo
                </Button>
              </div>
            </div>
            )
          ) : (
            <EmptyState onShowAddItemModal={onShowAddItemModal} />
          )}

          {/* Category Notes */}
          <div className="mt-4 pt-4 border-t border-[#DCDCDC]">
            <Label className="text-sm text-[#737373]">Note categoria {activeCategory}</Label>
            <Textarea
              value={formData.category_notes[activeCategory] || ''}
              onChange={(e) => onCategoryNotesChange(activeCategory, e.target.value)}
              placeholder="Aggiungi note per questa categoria..."
              className="mt-1"
              rows={2}
              data-testid="category-notes-textarea"
            />
          </div>

          {/* Category Totals */}
          {categoryTotals[activeCategory]?.itemCount > 0 && (
            <CategoryTotalsSection
              activeCategory={activeCategory}
              categoryTotals={categoryTotals}
              onCategoryDiscountChange={onCategoryDiscountChange}
              onCategoryFixedPriceChange={onCategoryFixedPriceChange}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Item row sub-component
const SortableItemRow = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#FFF7E6' : undefined,
  };

  return (
    <ItemRow
      {...props}
      dragRef={setNodeRef}
      dragAttributes={attributes}
      dragListeners={listeners}
      dragStyle={style}
    />
  );
};

const ItemRow = ({
  item,
  index,
  editingNoteId,
  setEditingNoteId,
  editingNoteValue,
  setEditingNoteValue,
  onUpdateItem,
  onRemoveItem,
  onAddBelow,
  canReorder = false,
  isFirst = false,
  isLast = false,
  onMoveUp,
  onMoveDown,
  dragRef,
  dragAttributes,
  dragListeners,
  dragStyle,
  electrical = false,
  roomOptions = [],
  onMoveRoom,
}) => {
  const isAutoItem = item.is_auto || item.locked || item.auto_rule_id;
  
  // Stato locale per input prezzo unit (solo per cat A/B editabili)
  const [localUnitPrice, setLocalUnitPrice] = useState(String(item.unit_price || 0));
  
  // Sync quando item.unit_price cambia dall'esterno
  React.useEffect(() => {
    setLocalUnitPrice(String(item.unit_price || 0));
  }, [item.unit_price]);
  
  const handleUnitPriceChange = (e) => {
    const normalized = normalizeNumericInput(e.target.value);
    setLocalUnitPrice(normalized);
  };
  
  const handleUnitPriceBlur = () => {
    const parsed = parseItalianNumber(localUnitPrice, 0);
    const finalValue = Math.max(0, parsed);
    setLocalUnitPrice(String(finalValue));
    onUpdateItem(item.id, 'unit_price', finalValue);
  };

  // STEP 2: Determina modalita quantita (backward compatible)
  const quantityMode = item.quantity_mode || 'single';
  const isDetailed = quantityMode === 'detailed' && !isAutoItem;
  const measurementLines = Array.isArray(item.measurement_lines) ? item.measurement_lines : [];
  const detailedTotalQty = isDetailed ? computeTotalFromMeasurementLines(measurementLines) : null;

  const handleModeChange = (newMode) => {
    if (newMode === quantityMode) return;
    if (newMode === 'detailed') {
      // Passa a detailed: inizializza measurement_lines vuoto se non presente
      onUpdateItem(item.id, 'quantity_mode', 'detailed');
      if (measurementLines.length === 0) {
        onUpdateItem(item.id, 'measurement_lines', []);
      }
    } else {
      // Ritorna a single: lascia measurement_lines per non perdere dati, ma usa quantity attuale
      onUpdateItem(item.id, 'quantity_mode', 'single');
    }
  };

  const handleMeasurementLinesChange = (newLines) => {
    const totalQty = computeTotalFromMeasurementLines(newLines);
    onUpdateItem(item.id, 'measurement_lines', newLines);
    // Aggiorna anche item.quantity quando in modalita detailed
    onUpdateItem(item.id, 'quantity', totalQty);
  };

  return (
    <>
    <tr 
      ref={dragRef}
      style={dragStyle}
      {...(dragAttributes || {})}
      className={`border-b border-[#E9E9E9] hover:bg-[#FAFAFA] [&>td]:py-3 ${isAutoItem ? 'bg-amber-50/50' : ''}`}
      data-testid={`item-row-${item.id}`}
    >
      <td className="py-2 px-2 text-[#737373] align-top">
        <div className="flex items-center gap-1">
          {canReorder && !isAutoItem && (
            <button
              type="button"
              {...(dragListeners || {})}
              className="cursor-grab active:cursor-grabbing text-[#BFBFBF] hover:text-[#FF7819] touch-none"
              title="Trascina per riordinare"
              data-testid={`drag-handle-${item.id}`}
              aria-label="Trascina voce"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
          {isAutoItem && (
            <Lock className="w-3 h-3 text-amber-600" title="Voce automatica" />
          )}
          <span>{index + 1}</span>
        </div>
      </td>
      <td className="py-2 px-2 align-top">
        <div className="text-[11px] font-semibold font-mono text-[#8A8A8A] tracking-tight" data-estimate-item-code>
          {item.code}
          {isAutoItem && (
            <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-normal">
              AUTO
            </span>
          )}
        </div>
        <div className="text-[14px] font-medium text-[#1F1F1F] mt-0.5" data-estimate-item-description>{item.functional_label || item.description}</div>
        {/* Formula display - solo modalita single */}
        {!isDetailed && item.quantity_formula && (
          <div className="mt-1 text-xs text-[#0078C8] font-mono bg-blue-50 border border-blue-100 rounded px-2 py-0.5 inline-block">
            Calcolo: {item.quantity_formula} = {item.quantity.toString().replace('.', ',')}
          </div>
        )}
        {/* STEP 2: Toggle modalita quantita (nascosto su voci AUTO) */}
        {!isAutoItem && (
          <div className="mt-2 inline-flex items-center gap-1 rounded-md border border-[#ECECEC] bg-white p-0.5 text-[11px]">
            <button
              type="button"
              onClick={() => handleModeChange('single')}
              className={`px-2 py-0.5 rounded transition-colors ${
                !isDetailed ? 'bg-[#FF7819] text-white font-medium' : 'text-[#737373] hover:bg-gray-50'
              }`}
              data-testid={`mode-single-${item.id}`}
            >
              Q.tà singola
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('detailed')}
              className={`px-2 py-0.5 rounded transition-colors flex items-center gap-1 ${
                isDetailed ? 'bg-[#FF7819] text-white font-medium' : 'text-[#737373] hover:bg-gray-50'
              }`}
              data-testid={`mode-detailed-${item.id}`}
            >
              <Ruler className="w-3 h-3" />
              Dettaglio misure
              {isDetailed && measurementLines.length > 0 && (
                <span className="ml-1 text-[10px] bg-white/30 px-1 rounded">{measurementLines.length}</span>
              )}
            </button>
          </div>
        )}
        {/* FASE 5: controlli elettrici (cambio ambiente + etichetta funzionale) */}
        {electrical && !isAutoItem && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1" title="Sposta in un altro ambiente">
              <ArrowRightLeft className="w-3 h-3 text-[#737373]" />
              <Select
                value={item.electrical_room_key || 'altre'}
                onValueChange={(v) => { const opt = roomOptions.find((o) => o.key === v); onMoveRoom && onMoveRoom(item.id, v, opt ? opt.label : v); }}
              >
                <SelectTrigger className="h-7 text-xs w-48" data-testid={`room-move-${item.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{roomOptions.map((o) => (<SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            {item.functional_label != null || electrical ? (
              <Input
                value={item.functional_label || ''}
                onChange={(e) => onUpdateItem(item.id, 'functional_label', e.target.value)}
                className="h-7 text-xs w-60"
                placeholder="Etichetta funzionale"
                data-testid={`functional-label-${item.id}`}
              />
            ) : null}
          </div>
        )}
        {/* Note editing */}
        <div className="mt-2">
          <NoteEditor
            item={item}
            editingNoteId={editingNoteId}
            setEditingNoteId={setEditingNoteId}
            editingNoteValue={editingNoteValue}
            setEditingNoteValue={setEditingNoteValue}
            onUpdateItem={onUpdateItem}
          />
        </div>
      </td>
      <td className="py-2 px-2 text-center align-middle text-[#1F1F1F]" data-estimate-item-unit>{item.unit}</td>
      <td className="py-2 px-2 align-middle">
        {isAutoItem ? (
          <div className="w-full h-8 flex items-center justify-center text-[#1F1F1F] bg-gray-100 rounded text-sm font-medium" data-estimate-item-qty-auto>
            {item.quantity.toString().replace('.', ',')}
          </div>
        ) : isDetailed ? (
          <div
            className="w-full h-8 flex items-center justify-center text-[#1F1F1F] bg-[#FF7819]/10 border border-[#FF7819]/30 rounded text-sm font-semibold"
            title="Quantità calcolata dal dettaglio misure"
            data-testid={`item-quantity-detailed-${item.id}`}
          >
            {(detailedTotalQty || 0).toString().replace('.', ',')}
          </div>
        ) : (
          <FormulaInput
            value={item.quantity}
            formula={item.quantity_formula || ''}
            onChange={(val, formula) => onUpdateItem(item.id, 'quantity', val, formula)}
            className="w-full text-center h-8 text-[#1F1F1F]"
            placeholder="1"
          />
        )}
      </td>
      <td className="py-2 px-2 align-middle">
        {isAutoItem ? (
          <div className="w-full h-8 flex items-center justify-end text-[#1F1F1F] bg-gray-100 rounded px-2 text-sm font-medium" data-estimate-item-price-auto>
            {formatCurrency(item.unit_price)}
          </div>
        ) : (item.category === 'A' || item.category === 'B') ? (
          <Input
            type="text"
            inputMode="decimal"
            value={localUnitPrice}
            onChange={handleUnitPriceChange}
            onBlur={handleUnitPriceBlur}
            className="w-full text-right h-8 text-[#1F1F1F]"
            data-testid={`item-unit-price-${item.id}`}
          />
        ) : (
          <div className="w-full h-8 flex items-center justify-end text-[#1F1F1F] bg-gray-50 rounded px-2 text-sm font-medium" data-estimate-item-price-readonly>
            {formatCurrency(item.unit_price)}
          </div>
        )}
      </td>
      <td className="py-2 px-2 text-right font-semibold text-[#1F1F1F] align-middle" data-estimate-item-total>{formatCurrency(item.total)}</td>
      <td className="py-2 px-2 align-middle">
        <div className="flex items-center gap-0.5">
          {/* STEP 1: Aggiungi sotto - azione discreta */}
          {onAddBelow && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onAddBelow(item.id)}
              className="text-[#737373] hover:text-[#FF7819] hover:bg-[#FF7819]/10 h-8 w-8 p-0"
              title="Aggiungi una voce subito sotto questa"
              data-testid={`add-below-${item.id}`}
            >
              <ArrowDownToLine className="w-4 h-4" />
            </Button>
          )}
          {canReorder && !isAutoItem && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onMoveUp}
                disabled={isFirst}
                className="text-[#737373] hover:text-[#FF7819] h-8 w-8 p-0 disabled:opacity-30"
                data-testid={`move-up-${item.id}`}
                title="Sposta su"
              >
                <ChevronUp className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onMoveDown}
                disabled={isLast}
                className="text-[#737373] hover:text-[#FF7819] h-8 w-8 p-0 disabled:opacity-30"
                data-testid={`move-down-${item.id}`}
                title="Sposta giu"
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
            </>
          )}
          {isAutoItem ? (
            <div className="w-8 h-8 flex items-center justify-center" title="Voce automatica non eliminabile">
              <Lock className="w-4 h-4 text-gray-300" />
            </div>
          ) : (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onRemoveItem(item.id)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
              data-testid={`remove-item-${item.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
    {/* STEP 2: Riga aggiuntiva con pannello dettaglio misure */}
    {isDetailed && (
      <tr className="border-b border-[#F2F2F2]" data-testid={`measurement-row-${item.id}`}>
        <td colSpan={7} className="p-2 bg-[#FAFAFA]">
          <MeasurementLinesEditor
            lines={measurementLines}
            unit={item.unit}
            onChange={handleMeasurementLinesChange}
          />
        </td>
      </tr>
    )}
    </>
  );
};

// === FASE 5: vista raggruppata per ambiente (categoria D elettrico) ===
const ElectricalGroupedView = ({
  items,
  collapsedGroups,
  setCollapsedGroups,
  editingNoteId,
  setEditingNoteId,
  editingNoteValue,
  setEditingNoteValue,
  onUpdateItem,
  onRemoveItem,
  onAddBelow,
  onMoveRoom,
  onAddItemToRoom,
}) => {
  const { intro, groups } = buildElectricalGroups(items);
  const roomOptions = (() => {
    const opts = groups.map((g) => ({ key: g.key, label: g.label }));
    if (!opts.find((o) => o.key === 'altre')) opts.push({ key: 'altre', label: 'Altre lavorazioni elettriche' });
    if (!opts.find((o) => o.key === 'generali')) opts.push({ key: 'generali', label: 'Dotazioni generali impianto' });
    return opts;
  })();
  let rowIndex = 0;

  return (
    <div className="space-y-3" data-testid="electrical-grouped-view">
      {/* D00 - capitolato introduttivo: full-width, non economico */}
      {intro.map((it) => (
        <div key={it.id} className="rounded-lg border border-[#FF7819]/30 bg-[#FFF7EE] p-3" data-testid="electrical-intro-block">
          <div className="text-[13px] font-semibold text-[#9A4B00] mb-1 flex items-center gap-2">
            <Lock className="w-3 h-3" /> {it.code} · Capitolato generale impianto
          </div>
          <div className="text-[13px] text-[#1F1F1F] whitespace-pre-line leading-relaxed">{it.description}</div>
        </div>
      ))}

      {groups.map((g) => {
        const collapsed = !!collapsedGroups[g.key];
        const isSpecial = g.key === 'generali' || g.key === 'altre';
        const headerLabel = isSpecial ? g.label : `Dotazione elettrica - ${g.label}`;
        return (
          <div key={g.key} className="border border-[#E5E5E5] rounded-lg overflow-hidden" data-testid={`electrical-group-${g.key}`}>
            <button
              type="button"
              onClick={() => setCollapsedGroups((s) => ({ ...s, [g.key]: !collapsed }))}
              className="w-full flex items-center justify-between px-3 py-2 bg-[#FAFAFA] hover:bg-[#F2F2F2]"
              data-testid={`electrical-group-toggle-${g.key}`}
            >
              <span className="flex items-center gap-2 font-semibold text-[#1F1F1F]">
                {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {headerLabel}
                <span className="text-xs font-normal text-[#737373]">({g.items.length})</span>
              </span>
              <span className="font-semibold text-[#FF7819]" data-testid={`electrical-group-subtotal-${g.key}`}>
                {formatCurrency(g.subtotal)}
              </span>
            </button>
            {!collapsed && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-fixed">
                  <colgroup>
                    {ESTIMATE_TABLE_COLS.map((col) => (<col key={col.key} style={{ width: col.width }} />))}
                  </colgroup>
                  <tbody>
                    {g.items.map((item) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        index={rowIndex++}
                        canReorder={false}
                        editingNoteId={editingNoteId}
                        setEditingNoteId={setEditingNoteId}
                        editingNoteValue={editingNoteValue}
                        setEditingNoteValue={setEditingNoteValue}
                        onUpdateItem={onUpdateItem}
                        onRemoveItem={onRemoveItem}
                        onAddBelow={onAddBelow}
                        electrical
                        roomOptions={roomOptions}
                        onMoveRoom={onMoveRoom}
                      />
                    ))}
                  </tbody>
                </table>
                {onAddItemToRoom && (
                  <div className="px-3 py-2 border-t border-dashed border-[#E5E5E5]">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#FF7819] hover:bg-[#FF7819]/10 h-7 text-xs"
                      data-testid={`electrical-group-add-${g.key}`}
                      onClick={() => onAddItemToRoom(g.key, g.label, g.items[0]?.electrical_group_id)}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi voce in {isSpecial ? g.label : g.label}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Note editor sub-component
const NoteEditor = ({
  item,
  editingNoteId,
  setEditingNoteId,
  editingNoteValue,
  setEditingNoteValue,
  onUpdateItem
}) => {
  if (editingNoteId === item.id) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={editingNoteValue}
          onChange={(e) => setEditingNoteValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onUpdateItem(item.id, 'notes', editingNoteValue);
              setEditingNoteId(null);
              setEditingNoteValue('');
            } else if (e.key === 'Escape') {
              setEditingNoteId(null);
              setEditingNoteValue('');
            }
          }}
          placeholder="Inserisci nota..."
          className="text-sm h-8 flex-1"
          autoFocus
        />
        <Button
          size="sm"
          className="h-8 px-2 bg-green-600 hover:bg-green-700"
          onClick={() => {
            onUpdateItem(item.id, 'notes', editingNoteValue);
            setEditingNoteId(null);
            setEditingNoteValue('');
          }}
        >
          <Check className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2"
          onClick={() => {
            setEditingNoteId(null);
            setEditingNoteValue('');
          }}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  if (item.notes) {
    return (
      <div 
        className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 cursor-pointer hover:bg-amber-100 transition-colors"
        onClick={() => {
          setEditingNoteId(item.id);
          setEditingNoteValue(item.notes);
        }}
      >
        <span className="font-bold text-amber-800">Nota:</span>{' '}
        <span className="italic text-amber-700">{item.notes}</span>
      </div>
    );
  }

  return (
    <button
      className="text-xs text-[#737373] hover:text-[#FF7819] flex items-center gap-1"
      onClick={() => {
        setEditingNoteId(item.id);
        setEditingNoteValue('');
      }}
    >
      <Plus className="w-3 h-3" />
      Aggiungi nota
    </button>
  );
};

// Empty state sub-component
const EmptyState = ({ onShowAddItemModal }) => (
  <div className="text-center py-8 text-[#737373]" data-testid="empty-category-state">
    <Calculator className="w-12 h-12 mx-auto mb-2 opacity-50" />
    <p>Nessuna voce in questa categoria</p>
    <Button 
      variant="outline" 
      className="mt-4"
      onClick={onShowAddItemModal}
    >
      <Plus className="w-4 h-4 mr-2" />
      Aggiungi la prima voce
    </Button>
  </div>
);

// Category totals section sub-component with dual input (Sconto % + Prezzo chiuso)
const CategoryTotalsSection = ({ activeCategory, categoryTotals, onCategoryDiscountChange, onCategoryFixedPriceChange }) => {
  const totals = categoryTotals[activeCategory];
  const subtotal = totals.subtotal;
  const isDisabled = subtotal === 0;
  const discountMode = totals.discountMode || 'percentage';
  
  // Local state for input values (allows Italian format typing)
  const [localDiscountValue, setLocalDiscountValue] = React.useState('');
  const [localFixedPriceValue, setLocalFixedPriceValue] = React.useState('');
  
  // Sync local state with props when mode or external values change
  React.useEffect(() => {
    if (discountMode === 'percentage') {
      setLocalDiscountValue(totals.discountPercentage ? String(totals.discountPercentage) : '');
    }
  }, [discountMode, totals.discountPercentage]);
  
  React.useEffect(() => {
    if (discountMode === 'fixed_price' && totals.fixedPrice !== null && totals.fixedPrice !== undefined) {
      setLocalFixedPriceValue(String(totals.fixedPrice).replace('.', ','));
    }
  }, [discountMode, totals.fixedPrice]);
  
  // Format percentage with max 2 decimals
  const formatPercentage = (value) => {
    if (!value || value === 0) return '';
    return Math.round(value * 100) / 100;
  };
  
  // Parse Italian number format
  const parseItalianNumber = (value) => {
    if (!value || value === '') return null;
    let str = String(value).trim();
    str = str.replace(/€/g, '').replace(/\s/g, '');
    if (!str) return null;
    
    const dots = (str.match(/\./g) || []).length;
    const commas = (str.match(/,/g) || []).length;
    
    if (commas === 1 && dots >= 1) {
      str = str.replace(/\./g, '');
      str = str.replace(',', '.');
    } else if (commas === 1 && dots === 0) {
      str = str.replace(',', '.');
    } else if (commas === 0 && dots > 1) {
      str = str.replace(/\./g, '');
    }
    
    const num = parseFloat(str);
    return isNaN(num) ? null : Math.round(num * 100) / 100;
  };
  
  // Handle discount percentage change
  const handleDiscountChange = (value) => {
    setLocalDiscountValue(value);
    const parsed = parseItalianNumber(value);
    if (parsed !== null || value === '') {
      onCategoryDiscountChange(activeCategory, parsed !== null ? String(parsed) : '');
    }
  };
  
  // Handle fixed price change
  const handleFixedPriceChange = (value) => {
    setLocalFixedPriceValue(value);
    const parsed = parseItalianNumber(value);
    if (parsed !== null || value === '') {
      onCategoryFixedPriceChange(activeCategory, parsed !== null ? String(parsed) : '');
    }
  };
  
  // Validate fixed price doesn't exceed subtotal (only in fixed_price mode)
  const isFixedPriceInvalid = discountMode === 'fixed_price' && totals.fixedPrice !== null && totals.fixedPrice > subtotal;

  // === SCONTO UNICO LAVORI C+D+E ===
  // Gross-only per C/D/E quando NON c'e' uno sconto per-categoria attivo
  // (nuovo schema o categoria pulita). Un preventivo storico con sconto
  // per singola categoria C/D/E (discountAmount>0) conserva i suoi controlli.
  const isWorksCategory = ['C', 'D', 'E'].includes(activeCategory) && !(totals.discountAmount > 0);
  if (isWorksCategory) {
    return (
      <div className="mt-4 pt-4 border-t border-[#DCDCDC]">
        <div className="flex justify-between items-center">
          <span className="text-[#737373] font-medium">Totale {activeCategory}</span>
          <span className="font-semibold text-lg" data-testid="category-gross-total">{formatCurrency(subtotal)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-[#DCDCDC] space-y-3">
      {/* Category gross total */}
      <div className="flex justify-between items-center">
        <span className="text-[#737373] font-medium">Totale {activeCategory}</span>
        <span className="font-semibold text-lg">{formatCurrency(subtotal)}</span>
      </div>
      
      {/* Discount inputs - both always visible */}
      <div className="bg-gray-50 rounded-lg p-3 space-y-3">
        {/* Row 1: Sconto % */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-1">
            <Label className="text-[#737373] whitespace-nowrap min-w-[80px]">Sconto %</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={discountMode === 'percentage' ? localDiscountValue : formatPercentage(totals.discountPercentageDisplay)}
              onChange={(e) => handleDiscountChange(e.target.value)}
              className={`w-24 h-9 text-right ${discountMode === 'percentage' ? 'border-[#FF7819] ring-1 ring-[#FF7819]/20' : ''}`}
              placeholder="0"
              disabled={isDisabled}
              data-testid="category-discount-input"
            />
          </div>
          <span className="text-sm text-[#737373]">oppure</span>
          <div className="flex items-center gap-2 flex-1 justify-end">
            <Label className="text-[#737373] whitespace-nowrap">Prezzo chiuso €</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={discountMode === 'fixed_price' ? localFixedPriceValue : (totals.fixedPrice ? String(Math.round(totals.fixedPrice * 100) / 100).replace('.', ',') : '')}
              onChange={(e) => handleFixedPriceChange(e.target.value)}
              className={`w-32 h-9 text-right ${discountMode === 'fixed_price' ? 'border-[#FF7819] ring-1 ring-[#FF7819]/20' : ''} ${isFixedPriceInvalid ? 'border-red-500' : ''}`}
              placeholder={formatCurrency(subtotal).replace('€', '').trim()}
              disabled={isDisabled}
              data-testid="category-fixed-price-input"
            />
          </div>
        </div>
        
        {/* Validation error */}
        {isFixedPriceInvalid && (
          <div className="text-red-500 text-sm">
            Il prezzo chiuso non può superare il totale categoria ({formatCurrency(subtotal)})
          </div>
        )}
        
        {/* Row 2: Sconto € calculated */}
        {totals.discountAmount > 0 && (
          <div className="flex justify-between items-center pt-2 border-t border-[#E5E5E5]">
            <span className="text-[#737373]">Sconto €</span>
            <span className="text-red-500 font-medium">-{formatCurrency(totals.discountAmount)}</span>
          </div>
        )}
      </div>
      
      {/* Discounted total */}
      <div className="flex justify-between items-center bg-[#FF7819] text-white px-4 py-3 rounded-lg">
        <span className="font-medium">Totale scontato {activeCategory}</span>
        <span className="font-bold text-xl" data-testid="category-discounted-total">
          {formatCurrency(totals.discountedTotal)}
        </span>
      </div>
    </div>
  );
};

export default EstimateCategoryContent;
